import { Inject, Injectable } from '@angular/core';
import { ILicenseFeatures, Organization, User } from '@telespot/sdk';
import { Observable } from 'rxjs';

import { CURRENT_ORGANIZATION, CURRENT_USER } from '../../tokens';

export type TFeatureName =
  | 'acquisition'
  | 'caseTypes'
  | 'ai'
  | 'secondOpinion'
  | 'exportWorkspace'
  | 'masks'
  | 'analysisReview'
  | 'reviewOthersAnalyses'
  | 'downloadAvailable';

@Injectable({
  providedIn: 'root',
})
export class FeaturesService {
  private _features: ILicenseFeatures;
  private _currentUser: User;

  // TODO: USE STORE
  constructor(
    @Inject(CURRENT_ORGANIZATION)
    private _currentOrganization$: Observable<Organization>,
    @Inject(CURRENT_USER) private _currentUser$: Observable<User>
  ) {
    this._currentOrganization$.subscribe((organization) => {
      this._features = organization?.license?.features;
    });
    this._currentUser$.subscribe((user) => (this._currentUser = user));
  }

  // WIP
  public canUse(feature: TFeatureName, subFeature?: string): boolean {
    switch (feature) {
      case 'acquisition':
        return (
          this._currentUser.isAdmin ||
          (this._currentUser.isTechnician && (this._features?.acquisition_available as any)?.enabled) ||
          (this._currentUser.isTechnicianManager && (this._features?.acquisition_available as any)?.enabled)
        );
      case 'caseTypes':
        return this._currentUser.isAdmin;
      case 'ai':
        // Admins always have AI available: they can see and review any analysis
        // regardless of the organization licence.
        if (this._currentUser?.isAdmin) return true;
        return typeof this._features?.ai === 'boolean'
          ? this._features?.ai
          : this._features?.ai?.[subFeature ?? 'enabled'];
      case 'secondOpinion':
        return typeof this._features?.second_opinion === 'boolean'
          ? this._features?.second_opinion
          : this._features?.second_opinion?.[subFeature ?? 'enabled'];
      case 'exportWorkspace':
        return typeof this._features?.export_workspace === 'boolean'
          ? this._features?.export_workspace
          : this._features?.export_workspace?.[subFeature ?? 'enabled'];
      case 'analysisReview':
        // Admins always review everything: they can see and review any analysis
        // on a sample regardless of the organization's review flags (same as AI).
        if (this._currentUser?.isAdmin) return true;
        return typeof this._features?.analysis_review === 'boolean'
          ? this._features?.analysis_review
          : this._features?.analysis_review?.[subFeature ?? 'enabled'];
      case 'reviewOthersAnalyses':
        // Whether the current user may see WHO ELSE analysed a case/sample.
        // Role-scoped exactly like the analysis screen scopes its analyst list
        // (`scopeAnalystsToOwn`): an analyst manager needs `manager_review`,
        // everyone else the plain `enabled` flag. Admins pass through the
        // override already built into 'analysisReview'.
        return this._currentUser?.is(['analystmanager'])
          ? this.canUse('analysisReview', 'manager_review')
          : this.canUse('analysisReview');
      case 'downloadAvailable': {
        const d = this._features?.download_available;
        if (d === undefined) return true;
        return typeof d === 'boolean' ? d : d?.enabled !== false;
      }
      case 'masks':
      default:
        return this._currentUser.isAdmin;
    }
  }
}
